import { vaultEngine } from "./vaultEngine";

const VAULT_SESSION_EVENT = "morfeusz:vault:session";
const VAULT_BRIDGE_VERSION = "vault-bridge-v1";
const LEGACY_VAULT_SOURCE = "legacy_morfeusz_vault";
const REST_VAULT_SOURCE = "rest_vault_engine";
const REST_SESSION_BLOB_KEY = "md_vault_session_blob_v1";

let initialized = false;
let bridgeState = {
  available: true,
  configured: false,
  unlocked: false,
  source: VAULT_BRIDGE_VERSION,
  reason: "vault_engine_not_connected",
};

const emitState = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(VAULT_SESSION_EVENT, { detail: getState() }));
};

const getState = () => ({ ...bridgeState });

const setState = (patch = {}) => {
  bridgeState = { ...bridgeState, ...patch, source: patch.source || bridgeState.source || VAULT_BRIDGE_VERSION };
  emitState();
  return getState();
};

const hasRestSessionBlob = () => {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.sessionStorage.getItem(REST_SESSION_BLOB_KEY));
  } catch (_) {
    return false;
  }
};

const markRestVaultUnlocked = () => {
  if (bridgeState.unlocked) return;
  setState({
    available: true,
    configured: true,
    unlocked: true,
    reason: "unlocked",
    source: REST_VAULT_SOURCE,
  });
};

const markRestVaultSessionIfPresent = () => {
  if (!hasRestSessionBlob()) return false;
  markRestVaultUnlocked();
  return true;
};

const getLegacyVault = () => {
  if (typeof window === "undefined") return null;
  const vault = window.morfeuszVault;
  if (!vault || typeof vault !== "object") return null;
  return vault;
};

const getLegacyVaultState = async () => {
  const vault = getLegacyVault();
  if (!vault) return null;

  const localState = typeof vault._state === "function" ? vault._state() : {};
  const unlocked = Boolean(localState?.unlocked);

  if (typeof vault.status === "function") {
    try {
      const status = await vault.status();
      return {
        available: true,
        configured: Boolean(status?.hasVault || status?.has_vault),
        unlocked,
        source: LEGACY_VAULT_SOURCE,
        reason: unlocked ? "unlocked" : "locked",
        legacy: status,
      };
    } catch (error) {
      return {
        available: true,
        configured: false,
        unlocked,
        source: LEGACY_VAULT_SOURCE,
        reason: error?.message || "legacy_vault_status_failed",
      };
    }
  }

  return {
    available: true,
    configured: false,
    unlocked,
    source: LEGACY_VAULT_SOURCE,
    reason: unlocked ? "unlocked" : "legacy_vault_status_missing",
  };
};

const getRestVaultState = async () => {
  try {
    const status = await vaultEngine.status();
    return {
      available: true,
      configured: Boolean(status?.hasVault || status?.has_vault),
      unlocked: Boolean(status?.unlocked),
      source: REST_VAULT_SOURCE,
      reason: status?.unlocked ? "unlocked" : "locked",
      rest: status,
    };
  } catch (error) {
    return {
      available: false,
      configured: false,
      unlocked: false,
      source: REST_VAULT_SOURCE,
      reason: error?.message || "rest_vault_status_failed",
    };
  }
};

const askVaultPassphrase = (message) => {
  if (typeof window === "undefined") return "";
  return window.prompt(message || "Podaj hasło sejfu Morfeusza:") || "";
};

const setupLegacyVault = async (vault) => {
  if (typeof window === "undefined") return { ok: false, reason: "window_missing" };
  if (typeof vault.setup !== "function") return { ok: false, reason: "legacy_vault_setup_missing" };

  const shouldCreate = window.confirm(
    "Nie masz jeszcze sejfu E2E. Utworzyć sejf teraz? Pamiętaj: tego hasła nie da się odzyskać.",
  );
  if (!shouldCreate) return { ok: false, reason: "vault_setup_cancelled" };

  const passphrase = askVaultPassphrase("Ustaw hasło sejfu E2E. Nie zapisuj go w miejscu publicznym.");
  if (!passphrase) return { ok: false, reason: "vault_setup_empty_passphrase" };

  const confirmPassphrase = askVaultPassphrase("Powtórz hasło sejfu E2E.");
  if (passphrase !== confirmPassphrase) return { ok: false, reason: "vault_setup_passphrase_mismatch" };

  const result = await vault.setup(passphrase);
  return { ok: Boolean(result?.ok ?? true), reason: "unlocked", legacy: result };
};

const setupRestVault = async () => {
  if (typeof window === "undefined") return { ok: false, reason: "window_missing" };
  const shouldCreate = window.confirm(
    "Nie masz jeszcze sejfu E2E. Utworzyć sejf teraz? Pamiętaj: tego hasła nie da się odzyskać.",
  );
  if (!shouldCreate) return { ok: false, reason: "vault_setup_cancelled" };

  const passphrase = askVaultPassphrase("Ustaw hasło sejfu E2E. Nie zapisuj go w miejscu publicznym.");
  if (!passphrase) return { ok: false, reason: "vault_setup_empty_passphrase" };

  const confirmPassphrase = askVaultPassphrase("Powtórz hasło sejfu E2E.");
  if (passphrase !== confirmPassphrase) return { ok: false, reason: "vault_setup_passphrase_mismatch" };

  try {
    const result = await vaultEngine.setup(passphrase);
    return { ok: Boolean(result?.ok ?? true), reason: "unlocked", rest: result };
  } catch (error) {
    return { ok: false, reason: error?.message || "vault_setup_failed" };
  }
};

const unlockLegacyVault = async (vault) => {
  if (typeof vault.unlock !== "function") return { ok: false, reason: "legacy_vault_unlock_missing" };

  const passphrase = askVaultPassphrase("Podaj hasło sejfu Morfeusza:");
  if (!passphrase) return { ok: false, reason: "vault_unlock_cancelled" };

  try {
    const result = await vault.unlock(passphrase);
    return { ok: Boolean(result?.ok ?? true), reason: "unlocked", legacy: result };
  } catch (error) {
    return { ok: false, reason: error?.message || "vault_unlock_failed" };
  }
};

const unlockRestVault = async () => {
  const passphrase = askVaultPassphrase("Podaj hasło sejfu Morfeusza:");
  if (!passphrase) return { ok: false, reason: "vault_unlock_cancelled" };

  try {
    const result = await vaultEngine.unlock(passphrase);
    return { ok: Boolean(result?.ok ?? true), reason: "unlocked", rest: result };
  } catch (error) {
    return { ok: false, reason: error?.message || "vault_unlock_failed" };
  }
};

const unlockRestVaultWithPassphrase = async (passphrase, options = {}) => {
  const cleanPassphrase = String(passphrase || "");
  if (!cleanPassphrase) return { ok: false, reason: "vault_empty_passphrase" };

  try {
    const state = await getRestVaultState();
    if (!state.available) {
      setState(state);
      return { ok: false, state: getState(), reason: state.reason || "rest_vault_unavailable" };
    }

    const result = state.configured
      ? await vaultEngine.unlock(cleanPassphrase)
      : options.createIfMissing === false
        ? { ok: false, reason: "vault_not_configured" }
        : await vaultEngine.setup(cleanPassphrase);

    const nextState = await getRestVaultState();
    const ok = Boolean(result?.ok && nextState?.unlocked);
    setState({
      available: true,
      configured: Boolean(nextState?.configured || state.configured || ok),
      unlocked: ok,
      reason: ok ? "unlocked" : (result?.reason || nextState?.reason || "rest_vault_locked"),
      source: REST_VAULT_SOURCE,
    });

    return { ok, state: getState(), rest: result };
  } catch (error) {
    setState({
      available: true,
      configured: true,
      unlocked: false,
      reason: error?.message || "vault_unlock_failed",
      source: REST_VAULT_SOURCE,
    });
    return { ok: false, state: getState(), reason: error?.message || "vault_unlock_failed" };
  }
};

const ensureLegacyVaultUnlocked = async () => {
  const vault = getLegacyVault();
  if (!vault) return null;

  const state = await getLegacyVaultState();
  if (state?.unlocked) {
    setState({ ...state, reason: "unlocked" });
    return { ok: true, state: getState(), legacy: state?.legacy };
  }

  const actionResult = state?.configured
    ? await unlockLegacyVault(vault)
    : await setupLegacyVault(vault);

  const nextState = await getLegacyVaultState();
  const ok = Boolean(actionResult?.ok && (nextState?.unlocked || vault._state?.()?.unlocked));
  setState({
    available: true,
    configured: Boolean(nextState?.configured || state?.configured || ok),
    unlocked: ok,
    reason: ok ? "unlocked" : (actionResult?.reason || nextState?.reason || "legacy_vault_locked"),
    source: LEGACY_VAULT_SOURCE,
  });

  return { ok, state: getState(), legacy: actionResult };
};

const ensureRestVaultUnlocked = async () => {
  const state = await getRestVaultState();
  if (state?.unlocked) {
    setState({ ...state, reason: "unlocked" });
    return { ok: true, state: getState(), rest: state?.rest };
  }

  if (!state?.available) {
    setState(state);
    return { ok: false, state: getState(), reason: state?.reason || "rest_vault_unavailable" };
  }

  const actionResult = state?.configured ? await unlockRestVault() : await setupRestVault();
  const nextState = await getRestVaultState();
  const ok = Boolean(actionResult?.ok && nextState?.unlocked);

  setState({
    available: true,
    configured: Boolean(nextState?.configured || state?.configured || ok),
    unlocked: ok,
    reason: ok ? "unlocked" : (actionResult?.reason || nextState?.reason || "rest_vault_locked"),
    source: REST_VAULT_SOURCE,
  });

  return { ok, state: getState(), rest: actionResult };
};

const ensureUnlocked = async () => {
  const external = typeof window !== "undefined" ? window.MDVaultSessionExternal : null;
  if (external && typeof external.ensureUnlocked === "function") {
    const result = await external.ensureUnlocked();
    const ok = Boolean(result?.ok || result?.unlocked || result?.state?.unlocked);
    setState({
      available: true,
      configured: Boolean(result?.state?.configured ?? true),
      unlocked: ok,
      reason: ok ? "unlocked" : (result?.reason || "external_vault_locked"),
      source: "external_vault_session",
    });
    return { ok, state: getState(), external: result };
  }

  const legacyResult = await ensureLegacyVaultUnlocked();
  if (legacyResult) return legacyResult;

  return ensureRestVaultUnlocked();
};

const ensureUnlockedWithPassphrase = async (passphrase, options = {}) => {
  const legacyVault = getLegacyVault();
  if (legacyVault && typeof legacyVault.unlock === "function") {
    try {
      const result = await legacyVault.unlock(passphrase);
      const ok = Boolean(result?.ok ?? true);
      setState({
        available: true,
        configured: true,
        unlocked: ok,
        reason: ok ? "unlocked" : "legacy_vault_locked",
        source: LEGACY_VAULT_SOURCE,
      });
      return { ok, state: getState(), legacy: result };
    } catch (error) {
      setState({
        available: true,
        configured: true,
        unlocked: false,
        reason: error?.message || "legacy_vault_unlock_failed",
        source: LEGACY_VAULT_SOURCE,
      });
      return { ok: false, state: getState(), reason: error?.message || "legacy_vault_unlock_failed" };
    }
  }

  return unlockRestVaultWithPassphrase(passphrase, options);
};

const lock = () => {
  const external = typeof window !== "undefined" ? window.MDVaultSessionExternal : null;
  if (external && typeof external.lock === "function") {
    external.lock();
  }

  const legacyVault = getLegacyVault();
  if (legacyVault && typeof legacyVault.lock === "function") {
    legacyVault.lock();
  }

  vaultEngine.lock();
  setState({ unlocked: false, reason: "locked" });
  return { ok: true, state: getState() };
};

const isUnlocked = () => Boolean(
  getState().unlocked
  || vaultEngine._state?.()?.unlocked
  || hasRestSessionBlob()
);

const isVaultAccessError = (error) => {
  const message = String(error?.message || "");
  return message === "vault_locked" || message === "bad_vault_session";
};

const publicSession = {
  ensureUnlocked,
  ensureUnlockedWithPassphrase,
  lock,
  getState,
  isUnlocked,
  eventName: VAULT_SESSION_EVENT,
};

const publicE2E = {
  encrypt: async (payload, aad = "") => {
    const vault = getLegacyVault();
    if (vault && typeof vault.encryptText === "function") {
      await ensureUnlocked();
      return vault.encryptText(String(payload || ""));
    }

    try {
      const encrypted = await vaultEngine.encryptText(String(payload || ""), aad);
      markRestVaultUnlocked();
      return encrypted;
    } catch (error) {
      if (!isVaultAccessError(error)) throw error;
      await ensureUnlocked();
      return vaultEngine.encryptText(String(payload || ""), aad);
    }
  },
  decrypt: async (iv, ct, aad = "") => {
    const vault = getLegacyVault();
    if (vault && typeof vault.decryptText === "function") {
      await ensureUnlocked();
      return vault.decryptText(iv, ct);
    }

    try {
      const plain = await vaultEngine.decryptText(iv, ct, aad);
      markRestVaultUnlocked();
      return plain;
    } catch (error) {
      if (!isVaultAccessError(error)) throw error;
      await ensureUnlocked();
      return vaultEngine.decryptText(iv, ct, aad);
    }
  },
  encryptText: async (payload, aad = "") => publicE2E.encrypt(payload, aad),
  decryptText: async (iv, ct, aad = "") => publicE2E.decrypt(iv, ct, aad),
  isUnlocked,
  getState,
};

const bindPokoikControls = () => {
  if (typeof document === "undefined") return;

  const renderStatus = () => {
    const state = getState();
    const text = state.unlocked
      ? "odblokowany"
      : state.source === VAULT_BRIDGE_VERSION
        ? "silnik sejfu niepodłączony"
        : state.configured
          ? "zablokowany"
          : "brak sejfu";
    document.querySelectorAll("#md-vault-status").forEach((node) => { node.textContent = text; });
  };

  renderStatus();

  document.querySelectorAll('[data-ui="md-e2e-unlock"]').forEach((button) => {
    if (button.dataset.mdVaultBridgeBound === "1") return;
    button.dataset.mdVaultBridgeBound = "1";
    button.addEventListener("click", async () => {
      await ensureUnlocked();
      renderStatus();
    });
  });

  document.querySelectorAll('[data-ui="md-e2e-lock"]').forEach((button) => {
    if (button.dataset.mdVaultBridgeBound === "1") return;
    button.dataset.mdVaultBridgeBound = "1";
    button.addEventListener("click", () => {
      lock();
      renderStatus();
    });
  });
};

export const initVaultBridge = () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  if (!window.MDVaultSession) {
    window.MDVaultSession = publicSession;
  }
  if (!window.morfeuszE2E) {
    window.morfeuszE2E = publicE2E;
  }

  markRestVaultSessionIfPresent();

  const bind = async () => {
    const legacyState = await getLegacyVaultState();
    if (legacyState) {
      setState(legacyState);
    } else {
      if (markRestVaultSessionIfPresent()) {
        getRestVaultState().then((state) => {
          if (!state.unlocked && hasRestSessionBlob()) return;
          setState(state);
          bindPokoikControls();
        });
        bindPokoikControls();
        return;
      }
      setState(await getRestVaultState());
    }
    bindPokoikControls();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bind, { once: true });
  } else {
    bind();
  }
  window.addEventListener("hashchange", () => window.setTimeout(bind, 0));
  window.addEventListener("focus", bind);
  window.addEventListener(VAULT_SESSION_EVENT, bindPokoikControls);
  window.addEventListener("morfeusz:vault:unlocked", bind);
  window.addEventListener("morfeusz:vault:locked", bind);
  window.addEventListener("morfeusz:vault:setup", bind);
};

export const VAULT_SESSION_EVENT_NAME = VAULT_SESSION_EVENT;
